import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ExpressionFunctionsConfig } from './expression-builder/expression-builder.component';

export interface AliasInfo {
  path: string;
  alias: string;
  repeatableParent?: string;
  parentVariable?: string;
  parentSegment?: string;
}

export interface RepeatableMappingResult {
  expression: string;
  isRepeatable: boolean;
  aliasInfo: AliasInfo;
}

export interface AddMappingDialogData {
  subcomponent: any;
  hl7Path: string;
  currentMappings: Array<{ xmlPath: string, condition: string, canEdit?: boolean }>;
  availableAliases: Array<AliasInfo>;
  isFromInterface: boolean;
  expressionFunctionsConfig?: ExpressionFunctionsConfig | null;
  // Segment context for repeatable mapping detection
  segmentHeader?: string;
  segmentLine?: string;
  getAliasForMapping?: (xmlPath: string) => string;
  getPendingChangeForMapping?: (segIndex: string, xmlPath: string, hl7Path: string) => { action: string, id: string } | null;
  stageMappingChange?: (segIndex: string, action: 'add' | 'delete' | 'update', mapping: { xmlPath: string, condition: string, hl7Path: string }, originalMapping?: { xmlPath: string, condition: string, hl7Path: string }) => void;
  removePendingChange?: (segIndex: string, changeId: string) => void;
  deleteMapping?: (mapping: { xmlPath: string, condition: string }) => Promise<any>;
  getMappingsForSubcomponent?: (subcomponent: any) => Array<{ xmlPath: string, condition: string, canEdit?: boolean }>;
  getConditionDisplay?: (condition: string) => string;
  getMethodDescription?: (condition: string) => string;
  // Callback to build repeatable mapping expression
  buildRepeatableMapping?: (aliasPath: string, segmentHeader: string, segIndex: string, segmentLine: string) => RepeatableMappingResult | null;
}

@Component({
  selector: 'app-add-mapping-dialog',
  templateUrl: './add-mapping-dialog.component.html',
  styleUrls: ['./add-mapping-dialog.component.scss']
})
export class AddMappingDialogComponent implements OnInit {
  selectedAliasPath: string = '';
  mappingCondition: string = '';
  isAddingMapping: boolean = false;
  editingMappingIndex: number | null = null;
  currentMappings: Array<{ xmlPath: string, condition: string, canEdit?: boolean }> = [];
  
  // Repeatable mapping tracking
  isRepeatableMapping: boolean = false;
  currentRepeatableInfo: RepeatableMappingResult | null = null;

  constructor(
    public dialogRef: MatDialogRef<AddMappingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddMappingDialogData,
    private toastr: ToastrService
  ) {
    this.currentMappings = [...data.currentMappings];
  }

  ngOnInit(): void {
  }

  getAlias(xmlPath: string): string {
    if (this.data.getAliasForMapping) {
      return this.data.getAliasForMapping(xmlPath);
    }
    // Fallback: extract last part of path
    const parts = xmlPath.split('/');
    return parts[parts.length - 1] || xmlPath;
  }

  addNewMapping(): void {
    this.isAddingMapping = true;
    this.editingMappingIndex = null;
    this.selectedAliasPath = '';
    this.mappingCondition = '';
    this.isRepeatableMapping = false;
    this.currentRepeatableInfo = null;
  }

  cancelEditMapping(): void {
    this.editingMappingIndex = null;
    this.isAddingMapping = false;
    this.selectedAliasPath = '';
    this.mappingCondition = '';
    this.isRepeatableMapping = false;
    this.currentRepeatableInfo = null;
  }

  onAliasChange(): void {
    // Check if selected alias is under a repeatable node and auto-construct expression
    this.isRepeatableMapping = false;
    this.currentRepeatableInfo = null;
    
    if (!this.selectedAliasPath || !this.data.buildRepeatableMapping) {
      return;
    }
    
    const segmentHeader = this.data.segmentHeader || '';
    const segIndex = this.data.subcomponent?.segindex || '';
    const segmentLine = this.data.segmentLine || '';
    
    const result = this.data.buildRepeatableMapping(
      this.selectedAliasPath,
      segmentHeader,
      segIndex,
      segmentLine
    );
    
    if (result) {
      this.currentRepeatableInfo = result;
      this.isRepeatableMapping = result.isRepeatable;
      
      // If repeatable, auto-populate the mapping condition with the constructed expression
      if (result.isRepeatable && result.expression) {
        this.mappingCondition = result.expression;
      }
    }
  }

  onExpressionChange(expression: string): void {
    // Update the mapping condition with the generated expression
    this.mappingCondition = expression;
  }

  async saveMapping(): Promise<void> {
    if (!this.selectedAliasPath || !this.data.subcomponent || !this.data.hl7Path) {
      this.toastr.error('Please select an alias to map to', '', {
        positionClass: 'toast-bottom-right',
        timeOut: 3000,
      });
      return;
    }

    if (!this.data.isFromInterface) {
      this.toastr.warning('Mapping can only be saved when viewing from an interface', '', {
        positionClass: 'toast-bottom-right',
        timeOut: 3000,
      });
      return;
    }

    if (!this.data.stageMappingChange) {
      this.toastr.error('Stage mapping function not available', '', {
        positionClass: 'toast-bottom-right',
        timeOut: 3000,
      });
      return;
    }

    const segIndex = this.data.subcomponent.segindex;
    const mapping = {
      hl7Path: this.data.hl7Path,
      xmlPath: this.selectedAliasPath,
      condition: this.mappingCondition || ''
    };

    // Check if this is an update (editing existing mapping)
    if (this.editingMappingIndex !== null && this.currentMappings[this.editingMappingIndex]) {
      const originalMapping = this.currentMappings[this.editingMappingIndex];
      const original = {
        hl7Path: this.data.hl7Path,
        xmlPath: originalMapping.xmlPath,
        condition: originalMapping.condition || ''
      };
      
      // Remove any existing pending change for this mapping first
      if (this.data.getPendingChangeForMapping && this.data.removePendingChange) {
        const existingPending = this.data.getPendingChangeForMapping(segIndex, originalMapping.xmlPath, this.data.hl7Path);
        if (existingPending) {
          this.data.removePendingChange(segIndex, existingPending.id);
        }
      }
      
      // Stage as update
      this.data.stageMappingChange(segIndex, 'update', mapping, original);
      this.toastr.success('Change staged. Click "Save All" to apply.', '', {
        positionClass: 'toast-bottom-right',
        timeOut: 2000,
      });
    } else {
      // Stage as add
      this.data.stageMappingChange(segIndex, 'add', mapping);
      this.toastr.success('Change staged. Click "Save All" to apply.', '', {
        positionClass: 'toast-bottom-right',
        timeOut: 2000,
      });
    }

    // Update current mappings display to show staged changes
    if (this.data.getMappingsForSubcomponent) {
      this.currentMappings = this.data.getMappingsForSubcomponent(this.data.subcomponent);
    }
    
    // Reset form
    this.cancelEditMapping();
  }

  async deleteMapping(index: number): Promise<void> {
    const mapping = this.currentMappings[index];
    if (!mapping) return;
    
    // Confirm deletion
    if (!confirm(`Stage deletion of mapping to "${this.getAlias(mapping.xmlPath)}"?\n\nClick "Save All" to apply.`)) {
      return;
    }
    
    if (this.data.deleteMapping) {
      // Pass the mapping object instead of index to avoid index issues after deletions
      await this.data.deleteMapping(mapping);
      // Refresh mappings after delete to reflect staged changes
      if (this.data.getMappingsForSubcomponent) {
        this.currentMappings = this.data.getMappingsForSubcomponent(this.data.subcomponent);
      }
    }
  }

  onClose(): void {
    // Return updated mappings when dialog closes
    this.dialogRef.close({ mappings: this.currentMappings });
  }

  getPendingChange(segIndex: string, xmlPath: string, hl7Path: string): { action: string, id: string } | null {
    if (this.data.getPendingChangeForMapping) {
      return this.data.getPendingChangeForMapping(segIndex, xmlPath, hl7Path);
    }
    return null;
  }

  removePending(segIndex: string, changeId: string): void {
    if (this.data.removePendingChange) {
      this.data.removePendingChange(segIndex, changeId);
      // Refresh mappings
      if (this.data.getMappingsForSubcomponent) {
        this.currentMappings = this.data.getMappingsForSubcomponent(this.data.subcomponent);
      }
    }
  }
}

